import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { Button, ErrorNote, Input } from "../components/ui";
import Mark from "../components/Mark";

/** Two steps, one field. There are no passwords anywhere in Atelier, and no
 *  organisation to name — the server derives one from the address on first
 *  sign-in and it is renameable in Settings. Every field on a sign-in form is
 *  a reason for someone to stop. */
export default function SignIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.requestCode(email.trim());
      setStep("code");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { token, new_account } = await api.auth.verify(email.trim(), code.trim());
      // Store the token BEFORE navigating: the route guard reads it
      // synchronously on mount, so navigating first lands on a guard that sees
      // no session and bounces straight back here.
      setToken(token);
      navigate(new_account ? "/app/new" : "/app", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-full items-center justify-center overflow-hidden px-5 py-16">
      {/* A single soft clay bloom. The sign-in page should feel like the same
          product as the homepage without paying for a WebGL context. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.13] blur-3xl"
        style={{ background: "radial-gradient(circle, rgb(var(--clay)) 0%, transparent 68%)" }}
      />

      <div className="relative w-full max-w-[22rem]">
        <div className="mb-9 text-center">
          <Link to="/" className="inline-block">
            <Mark className="mx-auto h-10 w-10" />
          </Link>
          <h1 className="mt-4 text-[1.35rem] font-semibold tracking-tight">
            {step === "email" ? "Sign in to Atelier" : "Check your email"}
          </h1>
          <p className="mx-auto mt-1.5 max-w-[19rem] text-sm leading-relaxed text-muted">
            {step === "email"
              ? "We'll email you a six-digit code. No password to remember."
              : `We sent a code to ${email.trim()}. It expires in ten minutes.`}
          </p>
        </div>

        {error ? (
          <div className="mb-4">
            <ErrorNote error={error} />
          </div>
        ) : null}

        {step === "email" ? (
          <form onSubmit={sendCode} className="flex flex-col gap-3">
            <Input
              value={email}
              onChange={setEmail}
              type="email"
              placeholder="you@company.com"
              autoFocus
            />
            <Button type="submit" disabled={busy || !email.includes("@")} className="w-full">
              {busy ? "Sending…" : "Continue"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="flex flex-col gap-3">
            <Input
              value={code}
              onChange={setCode}
              placeholder="123456"
              autoFocus
              mono
              className="text-center text-lg tracking-[0.5em]"
            />
            <Button type="submit" disabled={busy || code.trim().length < 6} className="w-full">
              {busy ? "Checking…" : "Sign in"}
            </Button>
            <button
              type="button"
              className="mt-1 text-xs text-muted transition hover:text-ink"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
            >
              Use a different email
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-2xs leading-relaxed text-muted">
          Running Atelier locally with no mailer? The code is written to the backend log.
        </p>
      </div>
    </div>
  );
}
